/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React, { useCallback } from 'react';
import { Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useContractForm } from './useContractForm';
import { Sanitize } from '../../../Components/Sanitizer';
import Subscriptions from './Components/Subscriptions';
import ContractFormSkeleton from './ContractFormSkeleton';
import './styles.scss';

/**
 * ContractForm Component
 * @param props
 * @returns {Element}
 * @constructor
 */
export default function ContractForm(props) {
  const { t } = useTranslation();

  const { values } = useFormikContext();
  const {
    secondStepContent, allowedContracts, oldContractTermPrice, formField: { contract },
  } = props;

  const removeNewLinesFromContract = useCallback((contracts) => {
    const parsedContracts = contracts.split(',');

    if (parsedContracts && parsedContracts.length > 0) {
      parsedContracts.forEach((allowedContract, index) => {
        parsedContracts[index] = allowedContract.replace(/(\r\n|\n|\r)/gm, '').trim();
      });
    }

    return parsedContracts;
  }, []);

  const {
    isLoading, contractData, tab, setTab,
  } = useContractForm(contract.name, allowedContracts ? removeNewLinesFromContract(allowedContracts) : []);

  /**
     * the dom section which will be loaded with a skeleton as loading indicator
     * @return {JSX.Element}
     */
  const renderFormElements = () => {
    // While loading return the loading overlay
    if (isLoading) {
      return <ContractFormSkeleton />;
    }

    return (
      <Grid container spacing={2}>
        {contractData ? (
          <Subscriptions
            contractData={contractData}
            contract={contract}
            oldContractTermPrice={oldContractTermPrice}
            tab={tab}
            setTab={setTab}
          />
        ) : null}
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>{t('currentSelection')}</Typography>
          <Typography variant="subtitle2">
            {t('yourSelection')}
            {' '}
            {values.studioName}
          </Typography>
        </Grid>
      </Grid>
      <Sanitize value={secondStepContent} />
      {renderFormElements()}
    </>
  );
}

ContractForm.propTypes = {
  secondStepContent: PropTypes.object.isRequired,
  allowedContracts: PropTypes.string.isRequired,
  oldContractTermPrice: PropTypes.string,
  formField: PropTypes.object.isRequired,
};

ContractForm.defaultProps = {
  oldContractTermPrice: null,
};
